import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Briefcase } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useServicesList,
  useCreateService,
  useUpdateService,
  useDeleteService,
  useServiceQuestionnaireAutomations,
  type ServiceRow,
  type ServiceInput,
} from '@/hooks/useServices';
import {
  useDeleteTreatmentCourseTemplate,
  useTreatmentCourseTemplates,
} from '@/hooks/useTreatmentCourseTemplates';
import {
  useReplaceServiceResourceRequirements,
  useServicePractitioners,
  useServiceResourceRequirements,
  type PractitionerEligibilityMode,
  type ServiceResourceRequirementInput,
} from '@/hooks/useSchedulingResources';
import {
  APPOINTMENT_TYPES_LIST_PATH,
  buildAppointmentTypeCatalog,
  catalogRowSummary,
  type AppointmentTypeCatalogRow,
} from '@/lib/appointmentTypeCatalog';
import { parsePractitionerEligibility } from '@/lib/serviceBookingEligibility';
import { DEFAULT_BLOCKER_FORM, DEFAULT_SERVICE_FORM, serviceInputFromRow } from '@/lib/serviceEditorDefaults';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Checkbox } from '@/components/ui/Checkbox';
import { StatusChip } from '@/components/ui/Chip';
import { ResponsiveTable } from '@/components/ui/Table';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SectionCard, SettingsEmpty, SettingsLoading } from './settingsChrome';
import { ServiceFormModal } from './ServiceFormModal';
import { useClinic } from '@/hooks/useClinicSettings';
import { clinicVatRatePercent, itemIncludesVat, itemVatRatePercent } from '@/lib/vat';
import {
  SERVICE_DURATION_MAX,
  SERVICE_DURATION_MIN,
  durationMinutesForSave,
  isInvalidServiceDurationError,
} from '@/lib/serviceDuration';

export function ClinicServices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));
  const { data: clinic } = useClinic(activeClinicId);
  const clinicVatRate = clinicVatRatePercent(clinic);

  const [includeInactive, setIncludeInactive] = useState(true);
  const {
    data: services,
    isLoading,
    error,
  } = useServicesList(activeClinicId, {
    includeInactive,
  });
  const {
    data: templates,
    isLoading: templatesLoading,
    error: templatesError,
  } = useTreatmentCourseTemplates(activeClinicId, { includeInactive });
  const create = useCreateService(activeClinicId);
  const update = useUpdateService(activeClinicId);
  const remove = useDeleteService(activeClinicId);
  const removeTemplate = useDeleteTreatmentCourseTemplate(activeClinicId);
  const replaceRequirements = useReplaceServiceResourceRequirements(activeClinicId);
  const { data: allRequirements } = useServiceResourceRequirements(activeClinicId);
  const { data: allServicePractitioners } = useServicePractitioners(activeClinicId);
  const { data: allAutomations } = useServiceQuestionnaireAutomations(activeClinicId);
  const catalog = useMemo(
    () =>
      buildAppointmentTypeCatalog({ services, templates }).filter(
        (row) => includeInactive || row.is_active,
      ),
    [includeInactive, services, templates],
  );

  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ServiceInput>(DEFAULT_SERVICE_FORM);
  const [creatingBlocker, setCreatingBlocker] = useState(false);
  const [requirements, setRequirements] = useState<ServiceResourceRequirementInput[]>([]);
  const [practitionerEligibility, setPractitionerEligibility] =
    useState<PractitionerEligibilityMode>('all');
  const [selectedPractitionerIds, setSelectedPractitionerIds] = useState<string[]>([]);

  const blockers = services?.filter((s) => s.is_blocker) ?? [];

  function openCreate() {
    navigate(`${APPOINTMENT_TYPES_LIST_PATH}/new`);
  }

  function openCreateBlocker() {
    setEditing(null);
    setCreatingBlocker(true);
    setForm(DEFAULT_BLOCKER_FORM);
    setRequirements([]);
    setPractitionerEligibility('all');
    setSelectedPractitionerIds([]);
    setShowForm(true);
  }

  function openEdit(svc: ServiceRow) {
    if (!svc.is_blocker) {
      navigate(`${APPOINTMENT_TYPES_LIST_PATH}/${svc.id}`);
      return;
    }
    setEditing(svc);
    setCreatingBlocker(true);
    const serviceAutomations = (allAutomations ?? []).filter((row) => row.service_id === svc.id);
    setForm(serviceInputFromRow(svc, serviceAutomations));
    setRequirements(
      (allRequirements ?? [])
        .filter((req) => req.service_id === svc.id)
        .map((req) => ({
          resource_group_id: req.resource_group_id,
          quantity: req.quantity,
          eligibility_mode: req.eligibility_mode,
          eligible_resource_ids: req.eligible_resource_ids,
        })),
    );
    setPractitionerEligibility(parsePractitionerEligibility(svc.practitioner_eligibility));
    setSelectedPractitionerIds(
      (allServicePractitioners ?? [])
        .filter((row) => row.service_id === svc.id)
        .map((row) => row.practitioner_id),
    );
    setShowForm(true);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!(e.currentTarget instanceof HTMLFormElement)) return;
    const duration_minutes = durationMinutesForSave(e.currentTarget, form.duration_minutes);
    if (duration_minutes == null) {
      toast.error(
        t('services.field_duration_invalid', {
          min: SERVICE_DURATION_MIN,
          max: SERVICE_DURATION_MAX,
        }),
      );
      return;
    }
    const payload = {
      ...form,
      duration_minutes,
      vat_rate_percent: itemVatRatePercent(itemIncludesVat(form.vat_rate_percent), clinicVatRate),
    };
    try {
      const saved = editing
        ? await update.mutateAsync({ id: editing.id, ...payload })
        : await create.mutateAsync(payload);
      if (!payload.is_blocker) {
        await replaceRequirements.mutateAsync({
          service_id: saved.id,
          practitioner_eligibility: practitionerEligibility,
          practitioner_ids: practitionerEligibility === 'selected' ? selectedPractitionerIds : [],
          requirements,
        });
      }
      toast.success(editing ? t('services.toast_updated') : t('services.toast_created'));
      setShowForm(false);
    } catch (err) {
      console.error('services save failed', err);
      toast.error(
        isInvalidServiceDurationError(err)
          ? t('services.field_duration_invalid', {
              min: SERVICE_DURATION_MIN,
              max: SERVICE_DURATION_MAX,
            })
          : t('services.toast_error'),
      );
    }
  }

  async function onDelete(svc: ServiceRow) {
    if (!confirm(t('services.confirm_delete', { name: svc.name }))) return;
    try {
      await remove.mutateAsync(svc.id);
      toast.success(t('services.toast_deleted'));
    } catch (err) {
      console.error('services delete failed', err);
      toast.error(t('services.toast_error'));
    }
  }

  async function onDeleteCatalog(row: AppointmentTypeCatalogRow) {
    if (!confirm(t('services.confirm_delete', { name: row.name }))) return;
    try {
      if (row.kind === 'service' && row.serviceId) {
        await remove.mutateAsync(row.serviceId);
      } else if (row.templateId) {
        await removeTemplate.mutateAsync(row.templateId);
      }
      toast.success(t('services.toast_deleted'));
    } catch (err) {
      console.error('appointment type delete failed', err);
      toast.error(t('services.toast_error'));
    }
  }

  if (isLoading || templatesLoading) {
    return <SettingsLoading />;
  }
  if (error || templatesError) return <ErrorAlert message={t('services.load_error')} />;

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <SectionCard
        title={t('services.title')}
        subtitle={t('services.subtitle')}
        readOnly={!canEdit}
      >
        <div className="mb-s4 flex flex-col gap-s3 nav:flex-row nav:items-center nav:justify-between">
          <Checkbox
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            label={t('services.show_inactive')}
          />
          {canEdit && (
            <Button onClick={openCreate} className="w-full nav:w-auto">
              <Plus className="h-4 w-4" />
              {t('services.new_service')}
            </Button>
          )}
        </div>

        {catalog.length === 0 ? (
          <SettingsEmpty icon={<Briefcase className="h-7 w-7" />} title={t('services.empty')} />
        ) : (
          <AppointmentTypeTable
            rows={catalog}
            canEdit={canEdit}
            onDelete={onDeleteCatalog}
          />
        )}
      </SectionCard>

      <SectionCard
        title={t('services.blockers_title', 'Kalender blokere')}
        subtitle={t(
          'services.blockers_subtitle',
          'Opret tidsblokke som frokost, møder, eller andre aktiviteter der skal blokkere kalenderen',
        )}
        readOnly={!canEdit}
      >
        {canEdit ? (
          <div className="mb-s4 flex justify-end">
            <Button onClick={openCreateBlocker} variant="secondary" className="w-full nav:w-auto">
              <Plus className="h-4 w-4" />
              {t('services.new_blocker', 'Ny blokering')}
            </Button>
          </div>
        ) : null}

        {!blockers || blockers.length === 0 ? (
          <SettingsEmpty
            icon={<Briefcase className="h-7 w-7" />}
            title={t('services.blockers_empty', 'Ingen blokeringer oprettet endnu')}
          />
        ) : (
          <ServiceTable
            services={blockers}
            canEdit={canEdit}
            onEdit={openEdit}
            onDelete={onDelete}
          />
        )}
      </SectionCard>

      {showForm && (
        <ServiceFormModal
          key={editing?.id ?? (creatingBlocker ? 'new-blocker' : 'new-service')}
          clinicId={activeClinicId ?? null}
          form={form}
          onChange={setForm}
          editing={editing}
          isBlocker={creatingBlocker}
          requirements={requirements}
          onRequirementsChange={setRequirements}
          practitionerEligibility={practitionerEligibility}
          onPractitionerEligibilityChange={setPractitionerEligibility}
          selectedPractitionerIds={selectedPractitionerIds}
          onSelectedPractitionerIdsChange={setSelectedPractitionerIds}
          onClose={() => setShowForm(false)}
          onSubmit={onSubmit}
          pending={create.isPending || update.isPending || replaceRequirements.isPending}
        />
      )}
    </div>
  );
}

function AppointmentTypeTable({
  rows,
  canEdit,
  onDelete,
}: {
  rows: AppointmentTypeCatalogRow[];
  canEdit: boolean;
  onDelete: (row: AppointmentTypeCatalogRow) => void;
}) {
  const { t } = useTranslation();
  return (
    <ResponsiveTable
      rows={rows}
      getRowKey={(row) => row.key}
      getRowHref={(row) => row.href}
      getRowLabel={(row) => row.name}
      columns={[
        {
          id: 'name',
          header: t('services.col_name'),
          cell: (row) => (
            <div className="min-w-0">
              <div className="flex items-center gap-s2">
                <span
                  className="inline-block h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: row.color }}
                  aria-hidden
                />
                <span className={cn(TYPE.body, 'text-ds-text')}>{row.name}</span>
              </div>
              {row.description ? (
                <div className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{row.description}</div>
              ) : null}
            </div>
          ),
        },
        {
          id: 'summary',
          header: t('services.col_summary'),
          cell: (row) =>
            catalogRowSummary(row, (count) => t('services.step_count', { count })),
        },
        {
          id: 'status',
          header: t('services.col_status'),
          cell: (row) => (
            <StatusChip tone={row.is_active ? 'completed' : 'neutral'}>
              {row.is_active ? t('services.status_active') : t('services.status_inactive')}
            </StatusChip>
          ),
        },
        {
          id: 'actions',
          header: t('services.col_actions'),
          cell: (row) =>
            canEdit ? (
              <Button
                type="button"
                variant="danger"
                size="icon"
                onClick={() => onDelete(row)}
                aria-label={t('services.delete')}
              >
                <Trash2 />
              </Button>
            ) : null,
        },
      ]}
    />
  );
}

function ServiceName({ svc }: { svc: ServiceRow }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-s2">
        <span
          className="inline-block h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: svc.color }}
          aria-hidden
        />
        <span className={cn(TYPE.body, 'text-ds-text')}>{svc.name}</span>
      </div>
      {svc.description ? (
        <div className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{svc.description}</div>
      ) : null}
    </div>
  );
}

function ServiceActions({
  svc,
  canEdit,
  onEdit,
  onDelete,
}: {
  svc: ServiceRow;
  canEdit: boolean;
  onEdit: (svc: ServiceRow) => void;
  onDelete: (svc: ServiceRow) => void;
}) {
  const { t } = useTranslation();
  if (!canEdit) return null;
  return (
    <div className="inline-flex gap-s2">
      <Button
        type="button"
        variant="tertiary"
        size="icon"
        onClick={() => onEdit(svc)}
        aria-label={t('services.edit')}
      >
        <Pencil />
      </Button>
      <Button
        type="button"
        variant="danger"
        size="icon"
        onClick={() => onDelete(svc)}
        aria-label={t('services.delete')}
      >
        <Trash2 />
      </Button>
    </div>
  );
}

function ServiceTable({
  services,
  canEdit,
  onEdit,
  onDelete,
}: {
  services: ServiceRow[];
  canEdit: boolean;
  onEdit: (svc: ServiceRow) => void;
  onDelete: (svc: ServiceRow) => void;
}) {
  const { t } = useTranslation();

  return (
    <ResponsiveTable
      rows={services}
      getRowKey={(svc) => svc.id}
      columns={[
        {
          id: 'name',
          header: t('services.col_name'),
          cell: (svc) => <ServiceName svc={svc} />,
        },
        {
          id: 'duration',
          header: t('services.col_duration'),
          cell: (svc) => `${svc.duration_minutes} ${t('services.minutes_short')}`,
        },
        {
          id: 'price',
          header: t('services.col_price'),
          numeric: true,
          hideOnMobile: true,
          cell: (svc) =>
            (svc.price_cents / 100).toLocaleString('da-DK', {
              style: 'currency',
              currency: svc.currency,
            }),
        },
        {
          id: 'vat',
          header: t('services.col_vat'),
          hideOnMobile: true,
          cell: (svc) =>
            itemIncludesVat(svc.vat_rate_percent) ? t('vat.with_vat') : t('vat.without_vat'),
        },
        {
          id: 'status',
          header: t('services.col_status'),
          cell: (svc) => (
            <StatusChip tone={svc.is_active ? 'completed' : 'neutral'}>
              {svc.is_active ? t('services.status_active') : t('services.status_inactive')}
            </StatusChip>
          ),
        },
        {
          id: 'actions',
          header: t('services.col_actions'),
          cell: (svc) => (
            <ServiceActions svc={svc} canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} />
          ),
        },
      ]}
    />
  );
}
